import "wicket/wicket-gmap3";
import "../../../shared/styles/form.css";

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Space,
  Spin,
  Table,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Wkt from "wicket";

import { createRegion, getRegions, Region } from "../../../services/regionsApi";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import ContainerLayout from "../../common/containerLayout";
import { DrawingMap } from "../../map/DrawingMap";
import { kml } from "@tmcw/togeojson";
import { ColumnsType } from "antd/es/table";
import { FeatureCollection, Polygon } from "geojson";
import EditableRegion from "../../../shared/interfaces/EditableRegion";
import { getRandomInt } from "../../../utils/helpers/numberUtils";

const colors = [
  "#b40056",
  "#3b185a",
  "#c02485",
  "#ffde14",
  "#e83278",
  "#009c9b",
  "#c6d76b",
  "#f39200",
  "#f8eb4c",
  "#546918",
  "#ff8379",
];

export const RegionForm = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [regions, setRegions] = useState<EditableRegion[]>([]);
  const [editableRegions, setEditableRegions] = useState<EditableRegion[]>([]);
  const [selectedRegionIndex, setSelectedRegionIndex] = useState<
    number | undefined
  >(undefined);
  const [currentPolygon, setCurrentPolygon] =
    useState<google.maps.Polygon | null>(null);
  const [center] = useState<google.maps.LatLngLiteral>({
    lat: -34.88582,
    lng: -56.17122,
  });

  const [regionCounter, setRegionCounter] = useState<number>(1);

  const columns: ColumnsType<EditableRegion> = [
    {
      dataIndex: "id",
      render: (id: number) => (
        <Radio
          checked={selectedRegionIndex === id}
          onChange={() => setSelectedRegionIndex(id)}
        />
      ),
      width: 80,
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      width: "60%",
      render: (text: string, record, index) => (
        <Input
          value={text}
          onChange={(e) => {
            const updated = [...editableRegions];
            updated[index].name = e.target.value;
            setEditableRegions(updated);
          }}
        />
      ),
    },
    {
      key: "actions",
      width: "20%",
      render: (_: any, record: EditableRegion) => (
        <Popconfirm
          title="¿Estás seguro de eliminar esta región?"
          onConfirm={() => {
            const updated = editableRegions.filter((x) => x.id !== record.id);
            removeRegionFromMap(record);
            setEditableRegions(updated);
          }}
        >
          <Button danger size="small">
            Eliminar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const removeRegionFromMap = (region: EditableRegion) => {
    region.polygon?.setMap(null);
    currentPolygon?.setMap(null);
    setCurrentPolygon(null);
  };

  const buildPolygonsPayload = (regions: Region[]) => {
    const paths = [];

    for (const region of regions) {
      try {
        const wkt = new Wkt.Wkt();
        wkt.read(region.polygon);

        const { coordinates } = wkt.toJson();

        const regionCoords = coordinates[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        paths.push({
          id: region.id,
          name: region.name,
          path: regionCoords,
        });
      } catch (error) {}
    }

    setRegions(paths);
  };

  const getRegionsFromApi = async () => {
    const data = await getRegions();
    buildPolygonsPayload(data);
    setLoadingRegions(false);
  };

  useEffect(() => {
    getRegionsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinishDrawing = (polygon: google.maps.Polygon, regionId?: number) => {
    const path: google.maps.LatLngLiteral[] = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

    const wkt = new Wkt.Wkt();
    wkt.fromObject(polygon);
    let counter = regionCounter;

    if (regionId !== undefined && regionId > -1) {
      const updated = [...editableRegions];
      let region = updated.find((x) => x.id === regionId);
      const color = colors[getRandomInt(colors.length)];

      if (!region) return;

      const polygon = new google.maps.Polygon({
        paths: path,
        strokeColor: "#000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35,
      });
      region.path = path;
      region.wkt = wkt.write();
      region.polygon = polygon;
      setEditableRegions(updated);
    } else {
      const color = colors[getRandomInt(colors.length)];

      const polygon = new google.maps.Polygon({
        paths: path,
        strokeColor: "#000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35,
      });
      const region = {
        id: counter,
        name: `Zona sin nombre ${counter}`,
        path,
        wkt: wkt.write(),
        polygon,
      };
      setEditableRegions((prev) => [...prev, region]);
      setSelectedRegionIndex(counter);
      ++counter;
      setRegionCounter(counter);
      notification.success({
        message: "Nuevo polígono agregado a la tabla",
        placement: "bottom",
      });
    }
  };

  const handleKmlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    let counter = regionCounter;

    reader.onload = (event) => {
      const kmlText = event.target?.result as string;
      const parser = new DOMParser();
      const xml = parser.parseFromString(kmlText, "text/xml");

      const geojson = kml(xml) as FeatureCollection;

      const newRegions: EditableRegion[] = geojson.features
        .filter((f) => f.geometry.type === "Polygon")
        .map((f) => {
          const coords = (f.geometry as Polygon).coordinates[0];
          const path = coords.map(([lng, lat]) => ({ lat, lng }));

          const name =
            f.properties?.name ||
            f.properties?.Name ||
            `Zona desde KML ${counter}`;

          const wkt = new Wkt.Wkt();
          wkt.fromJson(f.geometry);
          const wktText = wkt.write();
          const color = colors[getRandomInt(colors.length)];

          const polygon = new google.maps.Polygon({
            paths: path,
            strokeColor: "#000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.35,
          });

          const newRegion = {
            id: counter,
            name,
            path,
            wkt: wktText,
            polygon,
          };
          ++counter;

          return newRegion;
        });

      if (!newRegions.length) {
        setRegionCounter(1);
        notification.warning({
          message: "No se encontraron polígonos en el archivo KML",
          placement: "bottom",
        });
      } else {
        setRegionCounter(counter);
        setEditableRegions([...newRegions]);
        notification.success({
          message: `${newRegions.length} polígonos importados desde el archivo KML`,
          placement: "bottom",
        });
      }
    };

    reader.readAsText(file);
  };

  const onFinish = async () => {
    if (!editableRegions.length) {
      notification.warning({
        message: "Debes dibujar al menos una zona",
        placement: "bottom",
      });
      return;
    }

    try {
      setLoading(true);

      await Promise.all(
        editableRegions.map((region) => createRegion(region.name, region.wkt!)),
      );

      notification.success({
        message: `Las regiones se han creado exitosamente`,
        placement: "bottom",
        key: "global",
      });

      navigate(WebRoutes.RegionsRoute, {
        replace: true,
      });
    } catch (error) {
      notification.error({
        message: `Error al crear regiones`,
        placement: "bottom",
        key: "global",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMultiDelete = () => {
    Modal.confirm({
      title: "¿Seguro que deseas eliminar?",
      content: `Se eliminarán ${selectedRowKeys.length} registros.`,
      okText: "Sí, eliminar",
      cancelText: "Cancelar",
      onOk: () => {
        setLoading(true);
        try {
          setEditableRegions((prev) => {
            prev.forEach((item) => {
              if (selectedRowKeys.includes(item.id!)) {
                item.polygon?.setMap(null);
              }
            });

            return prev.filter((item) => !selectedRowKeys.includes(item.id!));
          });
          setSelectedRowKeys([]);
          notification.success({
            message: `Las regiones se han eliminado exitosamente`,
            placement: "bottom",
            key: "global",
          });
        } catch (e) {
          notification.error({
            message: `Error al eliminar las regiones`,
            placement: "bottom",
            key: "global",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleMultiSave = async () => {
    const regionsToSave: EditableRegion[] = [];
    setLoading(true);
    selectedRowKeys.forEach((regionId) => {
      const region = editableRegions.find((x) => x.id === Number(regionId));
      if (region) regionsToSave.push(region);
    });
    try {
      await Promise.all(
        regionsToSave.map((region) => createRegion(region.name, region.wkt!)),
      );

      notification.success({
        message: `Las regiones se han creado exitosamente`,
        placement: "bottom",
        key: "global",
      });
      setEditableRegions((prev) => {
        prev.forEach((item) => {
          if (selectedRowKeys.includes(item.id!)) {
            item.polygon?.setMap(null);
          }
        });

        return prev.filter((item) => !selectedRowKeys.includes(item.id!));
      });
      setSelectedRowKeys([]);
    } catch (e) {
      notification.error({
        message: `Error al crear regiones seleccionadas`,
        placement: "bottom",
        key: "global",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerLayout title="Crear nuevas regiones" showNotification={false}>
      <Col span={8}>
        <Form form={form} name="basic" onFinish={onFinish} autoComplete="off">
          <Form.Item label="Importar desde KML">
            <Input type="file" accept=".kml" onChange={handleKmlUpload} />
          </Form.Item>
          <Form.Item label="Zonas definidas:" labelCol={{ span: 24 }}>
            <Table
              columns={columns}
              dataSource={editableRegions.map((item, index) => ({
                ...item,
                key: index,
              }))}
              pagination={false}
              rowKey={(x) => x.id!}
              scroll={{ y: "calc(-250px + 80vh)" }}
              rowSelection={{
                type: "checkbox",
                columnWidth: "10%",
                selectedRowKeys,
                onChange: (newSelectedRowKeys: React.Key[]) =>
                  setSelectedRowKeys(newSelectedRowKeys),
              }}
            />
          </Form.Item>
          <Space direction="horizontal">
            <Button
              type="primary"
              onClick={handleMultiSave}
              disabled={!selectedRowKeys.length}
              loading={loading}
            >
              Guardar seleccionados
            </Button>
            <Button
              danger
              onClick={handleMultiDelete}
              disabled={!selectedRowKeys.length || loading}
            >
              Eliminar seleccionados
            </Button>
            <Button
              disabled={loading}
              onClick={() =>
                navigate(WebRoutes.RegionsRoute, {
                  replace: true,
                })
              }
            >
              Cancelar
            </Button>
            <Button
              disabled={!editableRegions.length}
              type="primary"
              htmlType="submit"
              loading={loading}
            >
              Guardar
            </Button>
          </Space>
        </Form>
      </Col>
      <Col span={16}>
        <Spin tip="Cargando regiones..." size="large" spinning={loadingRegions}>
          <DrawingMap
            zoom={12}
            center={center}
            regions={regions}
            editableRegions={editableRegions}
            drawable={true}
            selectedRegionId={selectedRegionIndex}
            currentPolygon={currentPolygon}
            setCurrentPolygon={setCurrentPolygon}
            onFinishDrawing={onFinishDrawing}
          />
        </Spin>
      </Col>
    </ContainerLayout>
  );
};

export default RegionForm;
